import { useMemo, useState } from 'react';
import { classNames, formatCurrency } from '@scripts/lib/formatters';

/**
 * Mount target:
 *   <div data-island="variation-selector" data-props="{...}"></div>
 *
 * Props contract:
 * - title: string
 * - selectPrompt: string
 * - unavailableMessage: string
 * - priceLabel: string
 * - currency: string
 * - locale: string
 * - initialVariationId: number
 * - attributes: Array<{ name: string, label: string, options: Array<{ value: string, label: string, disabled?: boolean }> }>
 * - variations: Array<{ id: number, inStock?: boolean, price?: number, formattedPrice?: string, sku?: string, attributes: Record<string, string> }>
 *
 * Trigger conditions:
 * - Render this island inside single product summary sections when the theme
 *   needs a richer variation chooser without replacing WooCommerce add-to-cart.
 */
export function VariationSelectorIsland({
  title = 'Choose Options',
  selectPrompt = 'Please select all options.',
  unavailableMessage = 'This combination is currently unavailable.',
  priceLabel = 'Selected Price',
  availableMessage = 'Available',
  currency = 'USD',
  locale = 'en',
  initialVariationId = 0,
  attributes = [],
  variations = [],
}) {
  const defaultSelections = useMemo(() => {
    const initialVariation = variations.find((variation) => Number(variation.id) === Number(initialVariationId));

    if (!initialVariation || typeof initialVariation.attributes !== 'object') {
      return {};
    }

    return initialVariation.attributes;
  }, [initialVariationId, variations]);
  const [selectedAttributes, setSelectedAttributes] = useState(defaultSelections);

  const selectedVariation = useMemo(() => {
    if (attributes.length === 0 || variations.length === 0) {
      return null;
    }

    const allSelected = attributes.every((attribute) => selectedAttributes[attribute.name]);

    if (!allSelected) {
      return null;
    }

    return (
      variations.find((variation) =>
        attributes.every(
          (attribute) => variation.attributes?.[attribute.name] === selectedAttributes[attribute.name]
        )
      ) || null
    );
  }, [attributes, selectedAttributes, variations]);

  function updateAttribute(name, value) {
    setSelectedAttributes((currentSelection) => ({
      ...currentSelection,
      [name]: value,
    }));
  }

  return (
    <section className="island-panel space-y-5">
      <header className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500">
          {selectedVariation ? selectedVariation.sku || '' : selectPrompt}
        </p>
      </header>

      <div className="space-y-4">
        {attributes.map((attribute) => (
          <section key={attribute.name} className="space-y-3">
            <header className="flex items-center justify-between gap-3">
              <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-600">
                {attribute.label}
              </h4>
              <span className="text-sm text-slate-500">
                {selectedAttributes[attribute.name] || ''}
              </span>
            </header>

            <div className="flex flex-wrap gap-2">
              {(attribute.options || []).map((option) => {
                const isActive = selectedAttributes[attribute.name] === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={Boolean(option.disabled)}
                    className={classNames(
                      'rounded-full border px-4 py-2 text-sm font-medium transition',
                      isActive
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-700',
                      option.disabled && 'cursor-not-allowed opacity-40'
                    )}
                    onClick={() => updateAttribute(attribute.name, option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <footer className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        {selectedVariation ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-slate-600">{priceLabel}</span>
              <span className="text-base font-semibold text-slate-900">
                {selectedVariation.formattedPrice ||
                  formatCurrency(selectedVariation.price, currency, locale)}
              </span>
            </div>
            <p
              className={classNames(
                'text-sm',
                selectedVariation.inStock === false ? 'text-rose-600' : 'text-emerald-600'
              )}
            >
              {selectedVariation.inStock === false ? unavailableMessage : availableMessage}
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{selectPrompt}</p>
        )}
      </footer>
    </section>
  );
}
